<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("customer_due_receive_entry") }}</h4>
        </div>
      </div>

      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/customer/due/receive/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitCustomerPayment" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-2 gx-4 mb-3 justify-content-between align-items-center">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:192px;">{{$t('voucher_no')}}:</label>
            <input type="text" class="form-control" v-model="customer.receiving_no" readonly/>
          </div>

          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:110px;">{{$t('voucher_date')}}:</label>
            <input type="date" class="form-control" v-model="customer.receiving_date" @change="fetchReceivingNo" />
            <div v-if="errors.receiving_date" class="text-danger mt-2">
              <span>{{errors.receiving_date}}</span>
            </div>
          </div>

          <div class="col-md-6 d-flex align-items-start">
            <label class="form-label small mb-0 me-3" style="width:126px;">{{$t('select_customer')}}:</label>
            <div class="flex-grow-1 position-relative">
              <!-- Search Input -->
              <input
                type="text"
                v-model="query"
                @input="searchCustomers"
                :placeholder="$t('search_customer_by_name_phone')"
                class="form-control"
                @keydown="handleKeys"
              />

              <!-- Dropdown Search Results -->
              <div
                v-if="query"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
              >
                <!-- Results List -->
                <ul class="list-group list-group-flush mb-0">
                  <li
                    v-for="(customer, index) in results"
                    :key="customer.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === customerIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                    @click="selectCustomer(customer)"
                  >
                    <div class="flex-grow-1">
                      <strong>{{ customer.customer_name }}</strong> - {{ customer.customer_phone }}
                    </div>
                  </li>
                </ul>

                <!-- No Results -->
                <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                  No results found
                </div>
              </div>
            </div>
          </div>

          <!-- Selected Customer Info -->
          <div class="col-md-6 d-flex align-items-center" v-if="selectedCustomer">
            <label class="form-label small mb-0 me-2">Customer Info:</label>
            <span style="font-size:1.1rem;">{{ selectedCustomer.customer_name }}</span>
            <span style="font-size:1.1rem; margin-right:.5rem;">[{{ selectedCustomer.customer_phone }}]</span>
            <span
              style="font-size:1.1rem;"
            >
              [<strong>Due:
                <span :style="{ color: selectedCustomer.due_balance > 0 ? 'red' : 'green' }">
                  ৳{{ selectedCustomer.due_balance || 0 }}
                </span>
              </strong>]
            </span>
          </div>
        </div>

        <!-- Customer Info Input Section -->
        <div class="row gy-2 gx-4 mb-3 p-3 mx-1 border rounded shadow-sm align-items-center">
          <!-- Narration -->
          <div class="col-md-6 d-flex mb-2">
            <label class="form-label small mb-0 me-3" style="width:95px; flex-shrink:0;">
              {{$t('narration')}}: <span class="text-danger">*</span>
            </label>
            <div class="flex-grow-1">
              <input
                type="text"
                class="form-control"
                placeholder="Narration"
                v-model="customer.narration"
              />
              <div class="text-danger" style="height:1.2em; font-size:0.875rem;">
                <span v-if="errors.narration">{{ errors.narration }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6 d-flex mb-2">
            <label class="form-label small mb-0 me-3" style="width:85px; flex-shrink:0;">
              {{$t('amount')}}:
            </label>
            <div class="flex-grow-1 d-flex flex-column">
              <div class="d-flex">
                <input
                  type="number"
                  class="form-control me-2"
                  placeholder="Amount"
                  v-model="customer.amount"
                />
                <button type="button" class="btn btn-info" @click="addCustomer">
                  {{$t('add')}}
                </button>
              </div>
              <div class="text-danger" style="height:1.2em; font-size:0.875rem;">
                <span v-if="errors.amount">{{ errors.amount }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive mb-3 fixed-table">
          <table class="table table-bordered table-striped align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 30%">{{$t('customer_name')}}</th>
                <th style="width: 30%">{{$t('narration')}}</th>
                <th style="width: 20%">{{$t('amount')}}</th>
                <th style="width: 20%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedCustomers" :key="index">
                <td>{{ cust.customer_name }}</td>
                <td>{{ cust.narration }}</td>
                <td>{{ cust.amount }}</td>
                <td class="text-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeCustomer(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedCustomers.length">
                <td colspan="11" class="text-center text-muted">No customers added</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment Type -->
        <div class="d-flex justify-content-between align-items-start mt-4">
          <div class="d-flex gap-4">
            <div class="d-flex flex-column">
              <label class="form-label">
                {{$t('payment_type')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="customer.cash_type"
                :options="formattedcashtypes"
                style="width: 300px;"
              />
              <div v-if="errors.cash_type" class="text-danger">{{ errors.cash_type }}</div>
            </div>

            <!-- Bank Accounts -->
            <div v-if="isBank" class="d-flex flex-column">
              <label class="form-label">
                {{$t('bank_account')}} <span class="text-danger">*</span>
              </label>
              <FormDropdown
                v-model="customer.bank_account"
                :options="formattedbankaccounts"
                style="width: 300px;"
              />
              <div v-if="errors.bank_account" class="text-danger">{{ errors.bank_account }}</div>
            </div>
          </div>

          <div class="d-flex flex-row gap-2 pe-2 mt-4">
            <button type="button" class="btn btn-cancel" @click="cancelForm">
              {{ $t('cancel') }}
            </button>

            <button type="submit" class="btn btn-submit" :disabled="saving">
              {{ $t('submit') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { todayLocal } from '@/utils';
import { apiErrorMessage } from '@/helpers/apiError';
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';

export default {
  components: {
    Vue3Datepicker,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      customer: {
        customer: null, narration: '', amount: '', cash_type: null,
        receiving_no: '', receiving_date: todayLocal(), bank_account: ''
      },
      cash_types: [],
      errors: {},
      errorMessage: "",
      saving: false,
      query: "",
      results: [],
      customerIndex: 0,
      selectedCustomer: null,
      addedCustomers: [],
      bank_accounts: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
  },

  computed: {
    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.customer.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },
  },

  methods: {
      async fetchDropdownData() {
        try {
          const [
            cash_typeRes, receive_noRes, bank_accRes,
          ] = await Promise.all([
            this.$axios.get(apiUrls.batch_cash_type.get),
            this.$axios.get(apiUrls.customer_due_receive_number.get),
            this.$axios.get(apiUrls.batch_bank_account.get)
          ]);

          this.cash_types = cash_typeRes?.data || [];
          this.customer.receiving_no = receive_noRes?.data?.receiving_no || '';
          this.bank_accounts = bank_accRes?.data || [];

          if (this.cash_types.length > 0 && !this.customer.cash_type) {
            this.customer.cash_type = this.cash_types[0].id;
          }

          if (this.bank_accounts.length > 0 && !this.customer.bank_account) {
            this.customer.bank_account = this.bank_accounts[0].id;
          }

        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      },

      async searchCustomers() {
        if (!this.query) {
          this.results = []
          return
        }

        try {
          const response = await this.$axios.get(apiUrls.customer_search.get, {
            params: { q: this.query }
          })
          const firstCustomerId = 1
          this.results = response.data.results.filter(c => c.id !== firstCustomerId);
          this.customerIndex = 0;

          // render feather icons
          await this.$nextTick(() => {
            if (window.feather) feather.replace()
          })
        } catch (error) {
          console.error('Search error:', error)
          this.results = []
        }
      },

      async selectCustomer(customer) {
        this.selectedCustomer = customer
        try {
          const response = await this.$axios.get(`/customer/due/${customer.id}`);
          this.selectedCustomer.due_balance = response.data.due_balance;
        } catch (error) {
          console.error("Error fetching due balance:", error);
          this.selectedCustomer.due_balance = 0;
        }
        this.query = customer.customer_name
        this.customer.customer = customer.id
        this.results = []
        this.customerIndex = 0;
      },

      handleKeys(event) {
        if (event.key === 'ArrowDown') {
          this.customerIndex = Math.min(this.customerIndex + 1, this.results.length - 1);
          event.preventDefault();
        } else if (event.key === 'ArrowUp') {
          this.customerIndex = Math.max(this.customerIndex - 1, 0);
          event.preventDefault();
        } else if (event.key === 'Enter') {
          const selectedProduct = this.results[this.customerIndex];
          if (selectedProduct) {
            this.selectCustomer(selectedProduct);
          }
          event.preventDefault();
        }
      },

      addCustomer() {
        if (!this.selectedCustomer) {
          this.toast.info("Please select an Customer first.");
          return;
        }

        // Prevent duplicate customer
        const exists = this.addedCustomers.some(
          c => c.id === this.selectedCustomer.id
        );

        if (exists) {
          this.toast.info("Customer already added.");
          return;
        }

        if (parseFloat(this.customer.amount) > parseFloat(this.selectedCustomer.due_balance)) {
          this.toast.info("Payment amount cannot exceed customer due amount");
          return;
        }

        // Frontend validation before adding to list
        this.errors = {};
        const requiredFields = [
          "customer", "narration", "receiving_date", 'amount'
        ];
        requiredFields.forEach(field => {
          if (!this.customer[field]) this.errors[field] = this.$t('error.required');
        });

        if (Object.keys(this.errors).length > 0) {
          return;
        }

        // Store full customer data in addedCustomers
        const newCustomer = {
          ...this.customer,
          id: this.selectedCustomer.id,
          customer_name: this.selectedCustomer.customer_name,
          customer_phone: this.selectedCustomer.customer_phone,
        };

        this.addedCustomers.push(newCustomer);

        // reset only row fields
        this.customer.supplier = null;
        this.customer.narration = "";
        this.customer.amount = "";

        // this.resetForm();
        this.errors = {};
        this.query = "";
        this.selectedCustomer = null;
      },

      async fetchReceivingNo() {
        try {
          let res = await this.$axios.get(apiUrls.customer_due_receive_number.get, {
            params: { receiving_date: this.customer.receiving_date }
          });
          this.customer.receiving_no = res.data.receiving_no;
        } catch (err) {
          console.error(err);
        }
      },

      async submitCustomerPayment() {
        if (this.saving) return;
        if (!this.addedCustomers.length) {
          this.toast.warning("Please add at least one Customer before submitting.");
          return;
        }

        const cashType = this.selectedId(this.customer.cash_type);
        const bankAccount = this.isBank ? this.selectedId(this.customer.bank_account) : null;
        if (!cashType) {
          this.errors = { cash_type: this.$t('error.required') };
          return;
        }
        if (this.isBank && !bankAccount) {
          this.errors = { bank_account: this.$t('error.required') };
          return;
        }

        this.saving = true;
        try {
          // Payment type is chosen below the table, usually after the rows are added, so it is
          // taken from the form at submit time rather than from each row.
          const payload = this.addedCustomers.map(row => ({
            ...row,
            cash_type: cashType,
            bank_account: bankAccount,
            receiving_no: this.customer.receiving_no,
            receiving_date: this.customer.receiving_date,
          }));
          await this.$axios.post(apiUrls.customer_payment.post, payload);

          this.toast.success("Customer payment saved successfully!");
          this.$router.push('/customer/due/receive/list');
          this.$emit("create_success");

          // Reset form after save
          this.customer = {
            customer: null, narration: '', amount: '', cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '',
            receiving_no: '', receiving_date: '', bank_account: ''
          };
          this.selectedCustomer = null;
          this.addedCustomers = [];

        } catch (error) {
          this.toast.error(apiErrorMessage(error));
          if (error.response?.data?.errors) {
            this.errors = error.response.data.errors;
          }
        } finally {
          this.saving = false;
        }
      },

      selectedId(value) {
        return value && typeof value === 'object' ? value.id : value;
      },

      cancelForm() {
        this.$router.push('/customer/due/receive/list');
      },

      removeCustomer(index) {
        this.addedCustomers.splice(index, 1);
      },

      resetForm() {
        // Reset form fields and errors
        this.customer = {
          customer: '', narration: '', cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '',
          receiving_no: '', receiving_date: '', bank_account: ''
        };
        this.errors = {};
        this.errorMessage = "";
      }
    },

  watch: {
    'customer.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.customer.receiving_no = '';
      }
    },

    'customer.cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.customer.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.customer.bank_account = null;
      }
    },

    isBank(newVal) {
      if (!newVal) this.customer.bank_account = null;
    },
  },
};
</script>


<style scoped>
/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

</style>

